import { lazy, type ComponentType } from "react";
import { CHUNK_RETRY_WINDOW_MS } from "./timing-constants";

/**
 * Storage key to track page reload attempts for failed chunk imports.
 * Prevents infinite refresh loops.
 */
const CHUNK_LOAD_FAILURE_KEY = "forgehq:chunk_load_retried";

/**
 * Check if we've recently attempted a reload for chunk failures
 */
function hasRecentlyRetried(): boolean {
  try {
    const lastRetry = sessionStorage.getItem(CHUNK_LOAD_FAILURE_KEY);
    if (!lastRetry) return false;

    // Consider retry "recent" if within the last 10 seconds
    const retryTime = parseInt(lastRetry, 10);
    return Date.now() - retryTime < CHUNK_RETRY_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Mark that we're attempting a reload for chunk failures
 */
function markRetryAttempt(): void {
  try {
    sessionStorage.setItem(CHUNK_LOAD_FAILURE_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear the retry marker (call this on successful page load)
 */
export function clearChunkRetryMarker(): void {
  try {
    sessionStorage.removeItem(CHUNK_LOAD_FAILURE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Dynamic imports can fail during module evaluation when an already-open tab
 * has a stale bundle graph. Safari reports that case as a ReferenceError such
 * as "Can't find variable: useCreateContributionBatch", rather than as a
 * chunk-fetch error. Treat only that browser/module-evaluation shape as
 * recoverable so ordinary runtime bugs are still surfaced normally.
 */
export function isRecoverableLazyImportError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const isModuleEvaluationReferenceError =
    error.name === "ReferenceError" &&
    (message.includes("can't find variable") ||
      message.includes("cannot find variable") ||
      message.includes("is not defined"));

  return (
    isModuleEvaluationReferenceError ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("importing a module script failed") ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("is not a valid javascript mime type")
  );
}

type PreloadableLazy<T extends ComponentType<any>> = React.LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>;
};

export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): PreloadableLazy<T> {
  let loadPromise: Promise<{ default: T }> | null = null;

  const load = () => {
    if (!loadPromise) {
      loadPromise = importFn().finally(() => {
        // Keep the promise cached for successful loads.
      });
    }

    return loadPromise.catch((error) => {
      loadPromise = null;
      throw error;
    });
  };

  const LazyComponent = lazy(async () => {
    try {
      return await load();
    } catch (error) {
      // Check if this is a chunk loading or stale-module evaluation error.
      const isChunkError = isRecoverableLazyImportError(error);

      if (isChunkError && !hasRecentlyRetried()) {
        // First, try a cache-busted dynamic re-import without a full page reload.
        // A full reload wipes in-memory auth tokens (persistSession: false) and
        // forces a bootstrap round-trip, which can fail and log the user out.
        markRetryAttempt();

        try {
          loadPromise = null; // clear cached failed promise
          const component = await importFn();
          // Retry succeeded without reloading the page, so clear the marker.
          clearChunkRetryMarker();
          return component;
        } catch {
          // In-process retry failed — fall back to a full page reload as a
          // last resort. The bootstrap flow will attempt to restore the session
          // from the HTTP-only refresh cookie.
          window.location.reload();
          return new Promise(() => {});
        }
      }

      // Either not a chunk error or we've already tried once - throw the original error
      throw error;
    }
  });

  (LazyComponent as PreloadableLazy<T>).preload = load;
  return LazyComponent as PreloadableLazy<T>;
}
